import React from "react";
import { Search, Send, Mic2 } from "lucide-react";

/**
 * Stylized phone mockup that shows guests what to do after scanning the QR.
 * Pulsing arrow points at the "ENVIAR" button.
 */
const PhoneMockup = () => {
  return (
    <div className="relative" data-testid="guest-phone-mockup">
      {/* Phone frame */}
      <div className="relative w-[240px] h-[480px] rounded-[36px] bg-[#1c1c1e] border-[6px] border-[#0a0a0a] shadow-[0_20px_60px_rgba(0,0,0,0.6),0_0_40px_rgba(5,217,232,0.25)] overflow-hidden">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80px] h-[20px] bg-[#0a0a0a] rounded-b-[14px] z-30" />

        {/* Status bar */}
        <div className="h-7 px-5 pt-1 flex items-center justify-between text-[9px] font-mono text-white/80 bg-[#0a0a0a]">
          <span>9:41</span>
          <span className="opacity-0">.</span>
          <span className="text-[8px]">●●● 5G</span>
        </div>

        {/* Mini header */}
        <div className="px-3 py-2 flex items-center gap-1.5 border-b border-white/10 bg-black/40">
          <Mic2 className="w-2.5 h-2.5 text-primary" />
          <p className="text-[9px] font-bold uppercase tracking-tight text-white">
            Hola <span className="text-primary">Tú</span>
          </p>
        </div>

        {/* Search */}
        <div className="px-3 py-3 border-b border-white/5">
          <p className="text-[7px] font-mono tracking-[0.25em] uppercase text-white/40 mb-1">
            /// Busca tu canción
          </p>
          <div className="relative">
            <Search className="w-2.5 h-2.5 absolute left-2 top-1/2 -translate-y-1/2 text-white/50" />
            <div className="w-full bg-white/[0.06] border border-white/20 rounded-md pl-6 pr-2 h-7 flex items-center text-[9px] font-mono text-white">
              Bohemian Rhapsody
            </div>
          </div>
        </div>

        {/* Mock result */}
        <div className="px-3 py-2 space-y-2">
          <div className="bg-white/[0.04] border border-white/10 rounded-md p-2 flex items-center gap-2">
            <div className="w-12 h-7 rounded-sm bg-gradient-to-br from-purple-700 via-fuchsia-700 to-amber-500 shrink-0 flex items-center justify-center">
              <span className="text-[7px] font-black text-white">QUEEN</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[8px] font-bold text-white truncate leading-tight">
                Bohemian Rhapsody Karaoke
              </p>
              <p className="text-[7px] text-white/50 truncate mt-0.5">Karaoke Version</p>
            </div>
            <div
              className="shrink-0 h-6 px-2 rounded-md font-bold uppercase tracking-wide text-[7px] flex items-center gap-1 bg-primary text-white shadow-[0_0_12px_rgba(255,42,109,0.7)] animate-glow-pulse"
              data-testid="phone-mockup-send"
            >
              <Send className="w-2 h-2" />
              Enviar
            </div>
          </div>

          {/* Second mock result (dimmed) */}
          <div className="bg-white/[0.03] border border-white/5 rounded-md p-2 flex items-center gap-2 opacity-60">
            <div className="w-12 h-7 rounded-sm bg-gradient-to-br from-rose-700 to-orange-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[8px] font-bold text-white truncate leading-tight">
                Don't Stop Me Now
              </p>
              <p className="text-[7px] text-white/40 truncate mt-0.5">Queen · Karaoke</p>
            </div>
            <div className="shrink-0 h-6 px-2 rounded-md font-bold uppercase text-[7px] flex items-center gap-1 bg-white/10 text-white/70">
              <Send className="w-2 h-2" />
              Enviar
            </div>
          </div>
        </div>
      </div>

      {/* Arrow pointing to the ENVIAR button */}
      <div className="absolute -right-32 top-[195px] flex items-center gap-3 pointer-events-none">
        {/* Arrow head + shaft */}
        <svg width="100" height="60" viewBox="0 0 100 60" className="text-primary -ml-3">
          <defs>
            <filter id="arrowGlow">
              <feGaussianBlur stdDeviation="3" />
            </filter>
          </defs>
          <g filter="url(#arrowGlow)" opacity="0.6">
            <path
              d="M95 30 Q 55 50, 25 30 Q 5 18, 8 12"
              stroke="currentColor"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
            />
            <path d="M95 30 L 80 22 M95 30 L 80 38" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </g>
          <g>
            <path
              d="M95 30 Q 55 50, 25 30 Q 5 18, 8 12"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeDasharray="6 4"
              className="animate-[dash_2s_linear_infinite]"
            />
            <path d="M95 30 L 80 22 M95 30 L 80 38" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </g>
        </svg>

        <div className="text-left">
          <p className="font-display font-black text-primary uppercase tracking-tighter text-3xl neon-pink leading-none">
            Toca<br />Enviar
          </p>
          <p className="font-mono text-[11px] text-white/60 tracking-widest uppercase mt-2 max-w-[220px]">
            La canción llegará al anfitrión y entrará a la cola al ser aprobada.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PhoneMockup;
